import { useState, useEffect } from "react";
import AdminSidebar from "../../../sidebar/admin_sidebar";
import AdminHeader from "../../../header/admin_header";
import { NavLink, useLocation } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";
import { Table, Modal, Button, Form } from "react-bootstrap";

const Unit = () => {
  const location = useLocation();
  const [unitSettings, setUnitSettings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUnitId, setDeleteUnitId] = useState(null);
  
  // Form states - Added 'id' to the structure
  const [formData, setFormData] = useState({
    id: null,
    category: "",
    subCategory: "",
    supportedUnits: [{ unit: "", factor: 1 }],
    baseUnit: "",
    status: true
  });
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle supported units changes
  const handleSupportedUnitChange = (index, field, value) => {
    const newSupportedUnits = [...formData.supportedUnits];
    newSupportedUnits[index] = {
      ...newSupportedUnits[index],
      [field]: field === 'factor' ? Number(value) : value
    };
    setFormData(prev => ({
      ...prev,
      supportedUnits: newSupportedUnits
    }));
  };

  const handleAddSupportedUnit = () => {
    setFormData(prev => ({
      ...prev,
      supportedUnits: [...prev.supportedUnits, { unit: "", factor: 1 }]
    }));
  };

  const handleRemoveSupportedUnit = (index) => {
    if (formData.supportedUnits.length > 1) {
      const newSupportedUnits = [...formData.supportedUnits];
      newSupportedUnits.splice(index, 1);
      setFormData(prev => ({
        ...prev,
        supportedUnits: newSupportedUnits
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.category.trim()) newErrors.category = "Category is required";
    if (!formData.baseUnit.trim()) newErrors.baseUnit = "Base unit is required";

    formData.supportedUnits.forEach((unit, index) => {
      if (!unit.unit.trim()) newErrors[`supportedUnit_${index}_unit`] = "Required";
      if (!unit.factor || unit.factor <= 0) newErrors[`supportedUnit_${index}_factor`] = "Invalid factor";
    });

    const isDuplicate = unitSettings.some(setting => 
      setting.category === formData.category && 
      setting.subCategory === formData.subCategory &&
      (!isEditing || setting.id !== editingUnitId)
    );

    if (isDuplicate) newErrors.category = "This combination already exists";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      id: null,
      category: "",
      subCategory: "",
      supportedUnits: [{ unit: "", factor: 1 }],
      baseUnit: "",
      status: true
    });
    setErrors({});
    setIsEditing(false);
    setEditingUnitId(null);
  };

const handleSubmit = async () => {
  if (!validateForm()) return;

  try {
    let success = false;
    if (isEditing) {
      // Add 'await' here and ensure the function returns the result
      success = await updateUnitSetting();
    } else {
      // Add 'await' here
      success = await createUnitSetting();
    }

    // Only close and reset if the API call was actually successful
    if (!!success) {
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    }
  } catch (error) {
    console.error("Error saving unit setting:", error);
  }
};

  const handleShowAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleShowEditModal = (unitSetting) => {
    setFormData({
      id: unitSetting.id, // Explicitly set ID here
      category: unitSetting.category,
      subCategory: unitSetting.subCategory || "",
      supportedUnits: unitSetting.supportedUnits || [{ unit: "", factor: 1 }],
      baseUnit: unitSetting.baseUnit,
      status: unitSetting.status
    });
    setEditingUnitId(unitSetting.id);
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleShowDeleteModal = (unitId) => {
    setDeleteUnitId(unitId);
    setShowDeleteModal(true);
  };

  const handleShowUnitsModal = (supportedUnits) => {
    setSelectedUnits(supportedUnits || []);
    setShowModal(true);
  };

  // API Calls
  const getUnitSettings = async () => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.ADMIN_API_URL}unitSettings`,
        {},
        {},
        "GET"
      );
      if (isSuccess && data) setUnitSettings(data || []);
    } catch (error) {
      console.error("Error fetching unit settings:", error);
    }
  };

  const createUnitSetting = async () => {
    const { isSuccess } = await apiCall(
      `${config.ADMIN_API_URL}unitSettings`,
      {},
      formData,
      "POST"
    );
    if (isSuccess) getUnitSettings();
  };

  const updateUnitSetting = async () => {
    // We use editingUnitId or formData.id to construct the URL
    const { isSuccess } = await apiCall(
      `${config.ADMIN_API_URL}unitSettings/${editingUnitId}`,
      {},
      formData,
      "PATCH"
    );
    if (isSuccess) {
      getUnitSettings();
    }
  };

  const deleteUnitSetting = async () => {
    try {
      const { isSuccess } = await apiCall(
        `${config.ADMIN_API_URL}unitSettings/${deleteUnitId}`,
        {},
        {},
        "DELETE"
      );
      if (isSuccess) {
        getUnitSettings();
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting unit setting:", error);
    }
  };

  useEffect(() => {
    getUnitSettings();
  }, []);

  const filteredUnitSettings = unitSettings.filter((setting) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      setting.category.toLowerCase().includes(searchLower) ||
      (setting.subCategory && setting.subCategory.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="steps-form">
            <div className="steps-row setup-panel">
              <div className="tabs-top setting_admin my-0">
                <ul>
                  <li><NavLink to="/settings"> My Profile </NavLink></li>
                  <li><NavLink to="/settings/billing"> Billing </NavLink></li>
                  <li><NavLink to="/settings/access_management"> Access </NavLink></li>
                  <li><NavLink to="/settings/emission-category">Emission Category</NavLink></li>
                  
                  <li><NavLink to="/settings/financial_year"> Financial Year </NavLink></li>

                  <li><NavLink to="/settings/sub-users">Sub Users</NavLink></li>
                  <li><NavLink to="/settings/category">Category</NavLink></li>
                  <li><NavLink to="/settings/carbon_emission"> Carbon Emission </NavLink></li>
                  <li><NavLink to="/settings/unit" className="activee"> Unit </NavLink></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="color_div_on framwork_2 pt-0">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: "400px" }}
              />
              <Button variant="primary" onClick={handleShowAddModal}> Add Unit Setting </Button>
            </div>

            <div className="business_detail">
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Sub Category</th>
                      <th>Base Unit</th>
                      <th>Supported Units</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUnitSettings.map((setting, index) => (
                      <tr key={setting.id}>
                        <td>{index + 1}</td>
                        <td>{setting.category}</td>
                        <td>{setting.subCategory || "-"}</td>
                        <td>{setting.baseUnit}</td>
                        <td>
                          <Button variant="link" onClick={() => handleShowUnitsModal(setting.supportedUnits)}>View</Button>
                        </td>
                        <td>
                          <span className={`badge ${setting.status ? 'bg-success' : 'bg-danger'}`}>
                            {setting.status ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <Button variant="warning" size="sm" className="me-2" onClick={() => handleShowEditModal(setting)}>Edit</Button>
                          <Button variant="danger" size="sm" onClick={() => handleShowDeleteModal(setting.id)}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton><Modal.Title>Supported Units</Modal.Title></Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead><tr><th>#</th><th>Unit</th><th>Factor</th></tr></thead>
            <tbody>
              {selectedUnits.map((u, i) => (
                <tr key={i}><td>{i+1}</td><td>{u.unit}</td><td>{u.factor}</td></tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal show={showAddModal || showEditModal} onHide={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit Unit Setting" : "Add New Unit Setting"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category *</Form.Label>
              <Form.Control 
                value={formData.category} 
                onChange={(e) => handleInputChange("category", e.target.value)} 
                isInvalid={!!errors.category} 
              />
              <Form.Control.Feedback type="invalid">{errors.category}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Sub Category *</Form.Label>
              <Form.Control 
                value={formData.subCategory} 
                onChange={(e) => handleInputChange("subCategory", e.target.value)} 
                isInvalid={!!errors.subCategory} 
              />
              <Form.Control.Feedback type="invalid">{errors.subCategory}</Form.Control.Feedback>
            </Form.Group>

            
            
            <Form.Group className="mb-3">
              <Form.Label>Base Unit *</Form.Label>
              <Form.Control 
                value={formData.baseUnit} 
                onChange={(e) => handleInputChange("baseUnit", e.target.value)} 
                isInvalid={!!errors.baseUnit} 
              />
            </Form.Group>

            <Form.Label>Supported Units</Form.Label>
            {formData.supportedUnits.map((unit, index) => (
              <div key={index} className="d-flex mb-2">
                <Form.Control 
                  placeholder="Unit" 
                  value={unit.unit} 
                  className="me-2"
                  onChange={(e) => handleSupportedUnitChange(index, "unit", e.target.value)}
                />
                <Form.Control 
                  type="number" 
                  placeholder="Factor" 
                  value={unit.factor} 
                  className="me-2"
                  onChange={(e) => handleSupportedUnitChange(index, "factor", e.target.value)}
                />
                <Button variant="outline-danger" onClick={() => handleRemoveSupportedUnit(index)}>-</Button>
              </div>
            ))}
            <Button size="sm" variant="outline-primary" onClick={handleAddSupportedUnit}>+ Add Unit</Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit}>{isEditing ? "Update" : "Add"}</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
        <Modal.Body>Delete this unit setting?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={deleteUnitSetting}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Unit;